/**
 * Widget Handler for Product Pages
 *
 * This file handles custom DOM events dispatched by the widget
 * embedded on product pages.
 */

// Widget event handlers will be implemented here

/**
 * @typedef {Object} FramesAddToCartDetail
 * @property {string|number=} variantId
 * @property {string|number=} productId
 * @property {Record<string, string>=} selectedOptions
 */

/**
 * @typedef {{ url: string }} FramesNavigateDetail
 */

/**
 * @typedef {{ url: string }} FramesPrefetchDetail
 */

/**
 * @typedef {{ routes?: { root?: string } }} ShopifyRoutesSource
 */

/**
 * @typedef {{
 *   price: number
 *   sku: string
 *   id: string | number
 *   options: string[]
 *   featured_image?: string | { src?: string }
 * }} ShopifyProductVariant
 */

/**
 * @returns {string}
 */
function getShopifyRoutesRoot() {
  const shopify = /** @type {ShopifyRoutesSource} */ (window.Shopify ?? {})
  return shopify.routes?.root ?? '/'
}

/**
 * @param {string | number | FramesAddToCartDetail | null | undefined} detail
 * @returns {string | null}
 */
function resolveVariantId(detail) {
  if (detail === null || typeof detail === 'undefined') {
    return null
  }

  if (typeof detail === 'string' || typeof detail === 'number') {
    return `${detail}`
  }

  if (typeof detail === 'object') {
    if (detail.variantId) {
      return `${detail.variantId}`
    }
    if (detail.productId) {
      return `${detail.productId}`
    }
  }

  return null
}

/**
 * @param {CustomEvent<string | number | FramesAddToCartDetail>} event
 */
async function handleAddToCartInit(event) {
  const variantId = resolveVariantId(event.detail)

  if (!variantId) {
    window.dispatchEvent(
      new CustomEvent('frames:addToCart:error', {
        detail: {
          error: 'Failed to add to cart',
        },
      }),
    )
    return
  }

  try {
    const resp = await fetch(getShopifyRoutesRoot() + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
      }),
    })
    if (!resp.ok) {
      throw new Error('Failed to add to cart')
    }
    const cartData = await getCartData()
    window.dispatchEvent(
      new CustomEvent('frames:addToCart:done', {
        detail: cartData.item_count,
      }),
    )
  } catch {
    window.dispatchEvent(
      new CustomEvent('frames:addToCart:error', {
        detail: {
          error: 'Failed to add to cart',
        },
      }),
    )
  }
}

window.addEventListener('frames:addToCart:init', handleAddToCartInit)

window.addEventListener('frames:fetchItems:init', async () => {
  try {
    const cartData = await getCartData()
    window.dispatchEvent(
      new CustomEvent('frames:fetchItems:done', {
        detail: cartData.item_count,
      }),
    )
  } catch (e) {
    window.dispatchEvent(
      new CustomEvent('frames:fetchItems:error', {
        detail: {
          error: 'Failed to fetch items',
        },
      }),
    )
  }
})

async function getCartData() {
  const cart = await fetch(getShopifyRoutesRoot() + 'cart.js', {
    cache: 'no-store',
  })
  const cartData = await cart.json()
  return cartData
}

function getProductId() {
  const addToCartForm = document.querySelector('form[action$="/cart/add"]')
  if (!(addToCartForm instanceof HTMLFormElement)) {
    throw new Error('Add to cart form not found')
  }
  const productId = new FormData(addToCartForm).get('product-id')
  if (!productId) {
    throw new Error('Product ID not found')
  }
  return `${productId}`
}

window.addEventListener('frames:fetchOptions:init', async () => {
  try {
    const productVariants = await getProductVariants()
    // Get currency from Shopify global or default to USD
    const currency = window.Shopify?.currency?.active || 'USD'
    window.dispatchEvent(
      new CustomEvent('frames:fetchOptions:done', {
        detail: {
          id: String(productVariants.variants[0].id),
          productId: String(productVariants.id),
          currency: currency,
          title: productVariants.title,
          description: productVariants.description,
          price: productVariants.price,
          options: productVariants.options,
          image: productVariants.featured_image,
          variants: productVariants.variants.map(
            /**
             * @param {ShopifyProductVariant} variant
             */
            (variant) => ({
              price: variant.price,
              sku: variant.sku,
              id: String(variant.id),
              options: variant.options,
              image: typeof variant.featured_image === 'string'
                ? variant.featured_image
                : variant.featured_image?.src || productVariants.featured_image,
            }),
          ),
        },
      }),
    )
  } catch {
    window.dispatchEvent(
      new CustomEvent('frames:fetchOptions:error', {
        detail: { error: 'Failed to fetch options' },
      }),
    )
  }
})

async function getProductVariants() {
  const currUrl = new URL(window.location.href)
  currUrl.searchParams.delete('variant')
  currUrl.pathname += '.js'
  const resp = await fetch(currUrl)
  const productVariants = await resp.json()
  return productVariants
}

/**
 * @param {CustomEvent<FramesNavigateDetail>} event
 */
function handleNavigate(event) {
  const { url } = event.detail
  window.location.assign(url)
}

window.addEventListener('frames:navigate:init', handleNavigate)

/**
 * @param {CustomEvent<FramesPrefetchDetail>} event
 */
async function handlePrefetch(event) {
  const { url } = event.detail
  try {
    const link = document.createElement('link')
    link.rel = 'prefetch'
    link.href = url
    document.head.appendChild(link)
    window.dispatchEvent(new CustomEvent('frames:prefetch:done'))
  } catch {
    window.dispatchEvent(
      new CustomEvent('frames:prefetch:error', {
        detail: { error: 'Failed to prefetch' },
      }),
    )
  }
}

window.addEventListener('frames:prefetch:init', handlePrefetch)

window.addEventListener('frames:navigate-cart', async () => {
  window.location.assign('/cart')
})

window.dispatchEvent(new CustomEvent('frames:widget-handler:ready'))
